import { CampaignPulseCard } from './CampaignPulseCard';
import { HeroMetricProps } from '../../../../components/Summary/HeroMetric';
import { RslReport } from '../../api/schema';
import {
  faEye,
  faUser,
  faShuffle,
  faStore,
} from '@fortawesome/pro-regular-svg-icons';
import { faDollar } from '@fortawesome/pro-solid-svg-icons';
import { Stack, Typography } from '@mui/material';
import { formatters } from '@utils/formattersV2';
import { SummaryCard, SummaryCardProps } from './SummaryCard';
import { memo } from 'react';

const pulseText = (hasVisitMetrics: boolean) =>
  hasVisitMetrics
    ? 'Get a highlight of your campaign Outcomes and Engagement Metrics. Deep dive into Real-Time Optimization and Campaign Analytics. '
    : 'Get a highlight of your campaign outcomes and input metrics. Deep dive into Real-Time Optimization.';

export type SummaryProps = {
  report: SummaryCardProps['report'] &
    Pick<
      RslReport,
      | 'incrementalSalesLiftConfidenceLevel'
      | 'matchedForAttribution'
      | 'impressions'
      | 'uniqueUsers'
      | 'projectedIncrementalTransactions'
      | 'projectedActualTransactions'
      | 'incrementalVisitLift'
      | 'incrementalVisitLiftConfidenceLevel'
      | 'projectedIncrementalVisits'
      | 'totalProjectedVisits'
    >;
  allReports: SummaryCardProps['allReports'];
  campaign: SummaryCardProps['campaign'];
};

export const Summary = memo((props: SummaryProps) => {
  const incrementalOutcomeMetrics: HeroMetricProps[] = [
    {
      value: props.report.incrementalSalesLift,
      valueFormatter: formatters.lift,
      confidenceLevel: props.report.incrementalSalesLiftConfidenceLevel,
      label: 'Incremental Sales Lift',
      colorScheme: 'sales',
      icon: faDollar,
      ['data-testid']: 'incremental-sales-lift-outcome',
    },
    {
      value: props.report.projectedIncrementalSales,
      valueFormatter: formatters.currency,
      label: 'Projected Incremental Sales',
      colorScheme: 'sales',
      icon: faDollar,
      ['data-testid']: 'incremental-sales-outcome',
    },
    {
      value: props.report.projectedIncrementalTransactions,
      valueFormatter: formatters.number,
      label: 'Incremental Transactions',
      colorScheme: 'sales',
      icon: faDollar,
      infoTooltipText:
        'Incr. Transactions = Incr. Sales / Avg. Transaction Size',
      ['data-testid']: 'incremental-transactions-outcome',
    },
    ...(props.report.hasVisitMetrics
      ? ([
          {
            value: props.report.incrementalVisitLift!,
            valueFormatter: formatters.lift,
            confidenceLevel: props.report.incrementalVisitLiftConfidenceLevel!,
            label: 'Incremental Visit Lift',
            colorScheme: 'visit',
            icon: faStore,
            ['data-testid']: 'incremental-visit-lift-outcome',
          },
          {
            value: props.report.projectedIncrementalVisits!,
            valueFormatter: formatters.visits,
            label: 'Incremental Visits',
            colorScheme: 'visit',
            icon: faStore,
            ['data-testid']: 'incremental-visits-outcome',
          },
        ] as const)
      : []),
  ];

  const mediaAttributedMetrics: HeroMetricProps[] = [
    {
      value: props.report.totalProjectedSales,
      valueFormatter: formatters.currency,
      label: 'Total Projected Sales',
      colorScheme: 'sales',
      icon: faDollar,
      size: 'sm',
      ['data-testid']: 'total-sales-outcome',
    },
    {
      value: props.report.projectedActualTransactions,
      valueFormatter: formatters.number,
      label: 'Total Transactions',
      colorScheme: 'sales',
      icon: faDollar,
      size: 'sm',
      infoTooltipText:
        'Total Transactions = Total Projected Sales / Avg. Transaction Size',
      ['data-testid']: 'total-transactions-outcome',
    },
    ...(props.report.hasVisitMetrics
      ? ([
          {
            value: props.report.totalProjectedVisits!,
            valueFormatter: formatters.visits,
            label: 'Total Visits',
            colorScheme: 'visit',
            icon: faStore,
            size: 'sm',
            ['data-testid']: 'total-visits-outcome',
          },
        ] as const)
      : []),
  ];

  const mediaInputMetrics: HeroMetricProps[] = [
    {
      value: props.report.impressions,
      valueFormatter: formatters.impressions,
      label: 'Impressions',
      colorScheme: 'plain',
      icon: faEye,
      size: 'sm',
      ['data-testid']: 'impressions-outcome',
    },
    {
      value: props.report.uniqueUsers,
      valueFormatter: formatters.users,
      label: 'Unique Users',
      colorScheme: 'plain',
      icon: faUser,
      size: 'sm',
      ['data-testid']: 'unique-users-outcome',
    },
    {
      value: props.report.matchedForAttribution,
      valueFormatter: formatters.users,
      label: 'Matched for Attribution',
      colorScheme: 'plain',
      icon: faShuffle,
      size: 'sm',
      ['data-testid']: 'matched-for-attribution-outcome',
    },
  ];

  return (
    <Stack direction="column" gap="1rem" flexWrap="wrap">
      <CampaignPulseCard
        incrementalOutcomeMetrics={incrementalOutcomeMetrics}
        mediaAttributedMetrics={mediaAttributedMetrics}
        mediaInputMetrics={mediaInputMetrics}
      >
        <Typography>{pulseText(props.report.hasVisitMetrics)}</Typography>
      </CampaignPulseCard>

      <SummaryCard
        report={props.report}
        allReports={props.allReports}
        campaign={props.campaign}
      />
    </Stack>
  );
});

Summary.displayName = 'Summary';
